import {
  StorageOptions,
  StorageStatus,
  StorageError,
  StorageErrorCode,
  type StorageResult,
  type DocumentWriteOptions,
} from "./types";
import { BaseStorage } from "./base-storage";
import { validationSystem } from "./validation";

/** WR-685 D9: an existence probe that has not settled by then answers "unknown". */
const HAS_KEY_TIMEOUT_MS = 2_000;
import {
  decideDocumentWrite,
  projectMetadataKey,
  type WriteDecision,
} from "../documentWriteGuard";
import { logger as _logger } from "../../utils/logger";

/**
 * IndexedDB implementation of StorageLayer
 */
export class IndexedDBManager extends BaseStorage {
  private readonly DB_NAME = "stagecraft_storage";
  private readonly STORE_NAME = "data_store";
  private readonly DB_VERSION = 1;
  private db: IDBDatabase | null = null;

  constructor() {
    super("indexedDB");
  }

  /**
   * Initialize database connection
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to open database",
            StorageErrorCode.NOT_AVAILABLE,
            { error: request.error },
          ),
        );
      };

      request.onsuccess = () => {
        const db = request.result;
        // SG-W3: if ANOTHER tab upgrades the DB, close + clear this cached
        // connection so it neither blocks the upgrade nor serves a stale handle
        // (the next getStore re-opens).
        db.onversionchange = () => {
          db.close();
          this.db = null;
        };
        this.db = db;
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };
    });
  }

  /**
   * Get database connection
   */
  private async getDB(): Promise<IDBDatabase> {
    try {
      return await this.initDB();
    } catch (error) {
      throw new StorageError(
        "Database not available",
        StorageErrorCode.NOT_AVAILABLE,
        { error },
      );
    }
  }

  /**
   * Create transaction and get object store
   */
  private async getStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.getDB();
    const transaction = db.transaction(this.STORE_NAME, mode);
    return transaction.objectStore(this.STORE_NAME);
  }

  /**
   * SG-W2: reject a pending operation if its TRANSACTION aborts (e.g. quota
   * exceeded, or a versionchange forcing close). A transaction-level abort may
   * not surface via the request's `onerror`, so without this the awaiting caller
   * would hang forever. Whichever of request/transaction settles the promise
   * first wins; a later reject on an already-settled promise is a no-op.
   */
  private rejectOnTxAbort(
    store: IDBObjectStore,
    reject: (reason: unknown) => void,
  ): void {
    // A real IDBObjectStore always exposes `.transaction`; guard anyway so a
    // hand-rolled test mock without one is a no-op rather than a throw.
    const tx = store.transaction;
    if (!tx) return;
    tx.onabort = () =>
      reject(
        new StorageError(
          "IndexedDB transaction aborted",
          StorageErrorCode.OPERATION_FAILED,
          { error: tx.error },
        ),
      );
  }

  /**
   * Get storage status
   */
  async getStatus(): Promise<StorageStatus> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction(this.STORE_NAME, "readonly");
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve) => {
        // Get count of items
        const countRequest = store.count();

        countRequest.onsuccess = () => {
          resolve({
            available: true,
            usedSpace: 0, // IndexedDB doesn't provide space usage
            totalSpace: Infinity, // IndexedDB uses available disk space
            operations: {
              pending: 0, // IndexedDB doesn't expose pending operations
              completed: countRequest.result,
              failed: 0,
            },
            health: {
              errorRate: 0,
              avgOperationTime: 0,
            },
          });
        };

        countRequest.onerror = () => {
          resolve({
            available: false,
            usedSpace: 0,
            totalSpace: 0,
            errorMessage: "Failed to get storage status",
          });
        };
      });
    } catch (error) {
      return {
        available: false,
        usedSpace: 0,
        totalSpace: 0,
        errorMessage: "Database not available",
      };
    }
  }

  /**
   * Implement storage-specific save operation
   */
  protected async performSave<T>(
    key: string,
    data: T,
    _options?: StorageOptions,
  ): Promise<void> {
    const store = await this.getStore("readwrite");

    return new Promise((resolve, reject) => {
      const request = store.put(data, key);
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to save data",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * Implement storage-specific load operation
   */
  protected async performLoad<T>(
    key: string,
    _options?: StorageOptions,
  ): Promise<T> {
    const store = await this.getStore("readonly");

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => {
        if (request.result === undefined) {
          reject(
            new StorageError("Data not found", StorageErrorCode.NOT_AVAILABLE),
          );
        } else {
          resolve(request.result);
        }
      };

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to load data",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * Implement storage-specific delete operation
   */
  protected async performDelete(
    key: string,
    _options?: StorageOptions,
  ): Promise<void> {
    const store = await this.getStore("readwrite");

    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to delete data",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * Implement storage-specific clear operation
   */
  protected async performClear(_options?: StorageOptions): Promise<void> {
    const store = await this.getStore("readwrite");

    return new Promise((resolve, reject) => {
      const request = store.clear();
      this.rejectOnTxAbort(store, reject);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to clear storage",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }

  /**
   * GUARDED document write (WR-685 hotfix, D2): ONE read-write transaction reads the
   * project's metadata record (liveness) and the stored value at `key` (currency),
   * asks `decideDocumentWrite` (the ONE authority — this method is rule-agnostic), and
   * then either puts (plus the empty-transition pre-image, when asked) or puts NOTHING.
   * A refusal commits an empty transaction — it is never an abort, so it is never
   * mistaken for a storage failure and retried (R4c2-4). Settles on the transaction's
   * `complete` / `abort` / `error` (SG-W2), so the caller never hangs.
   *
   * The legacy fallback for liveness (a project whose metadata the boot migration left
   * in localStorage) is read here, synchronously inside the request callback, only
   * when IndexedDB holds no metadata record — the document loader's tier order
   * (`coordinator.loadDocument`).
   */
  async guardedSave<T>(
    key: string,
    data: T,
    write: DocumentWriteOptions = {},
  ): Promise<StorageResult<void>> {
    const keyValidation = validationSystem.validateKey(key);
    const dataValidation = validationSystem.validateData(data);
    if (!keyValidation.isValid || !dataValidation.isValid) {
      return {
        success: false,
        error: new StorageError(
          "Invalid document write",
          StorageErrorCode.VALIDATION_FAILED,
          { errors: [...keyValidation.errors, ...dataValidation.errors] },
        ),
      };
    }

    let db: IDBDatabase;
    try {
      db = await this.getDB();
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof StorageError
            ? error
            : new StorageError(
                "Database not available",
                StorageErrorCode.NOT_AVAILABLE,
                { error },
              ),
      };
    }

    return new Promise<StorageResult<void>>((resolve) => {
      let settled = false;
      const settle = (r: StorageResult<void>) => {
        if (settled) return;
        settled = true;
        resolve(r);
      };
      const fail = (message: string, error: unknown) =>
        settle({
          success: false,
          error: new StorageError(message, StorageErrorCode.OPERATION_FAILED, {
            error,
          }),
        });

      let decision: WriteDecision = {
        kind: "failed",
        error: new Error("undecided"),
      };
      let preimage: unknown = undefined;

      let tx: IDBTransaction;
      try {
        tx = db.transaction(this.STORE_NAME, "readwrite");
      } catch (error) {
        fail("IndexedDB transaction could not start", error);
        return;
      }
      tx.oncomplete = () => {
        if (decision.kind === "allow") {
          settle({
            success: true,
            metadata: {
              timestamp: Date.now(),
              operation: "save",
              rev: decision.rev,
              preimage,
            },
          });
        } else if (decision.kind === "noop") {
          // Identical to what is stored: nothing was put, the revision did not move.
          settle({
            success: true,
            metadata: { timestamp: Date.now(), operation: "save", rev: decision.rev },
          });
        } else if (decision.kind === "refuse") {
          settle({
            success: false,
            error: new StorageError(
              "Document write refused",
              StorageErrorCode.WRITE_REFUSED,
              { reason: decision.reason, key },
            ),
            metadata: {
              timestamp: Date.now(),
              operation: "save",
              refusal: decision.reason,
            },
          });
        } else {
          fail("Document write undecided", decision.error);
        }
      };
      tx.onabort = () => fail("IndexedDB transaction aborted", tx.error);
      tx.onerror = () => fail("IndexedDB transaction failed", tx.error);

      const store = tx.objectStore(this.STORE_NAME);
      const projectId = write.guard?.projectId;
      const metaKey = projectId ? projectMetadataKey(projectId) : null;
      const metaReq = metaKey ? store.get(metaKey) : null;
      const storedReq = store.get(key);
      storedReq.onsuccess = () => {
        try {
          let metadataExists = !!metaReq && metaReq.result !== undefined;
          if (!metadataExists && metaKey) {
            try {
              metadataExists = localStorage.getItem(metaKey) !== null;
            } catch {
              // localStorage unavailable — no legacy copy to honour.
            }
          }
          const stored = storedReq.result;
          decision = decideDocumentWrite(
            key,
            data,
            write.guard,
            metadataExists,
            stored,
          );
          if (decision.kind === "allow") {
            store.put(decision.value, key);
            if (write.preimage) {
              try {
                const p = write.preimage.of(stored);
                if (p !== undefined) {
                  store.put(p, write.preimage.key);
                  preimage = p;
                }
              } catch (journalError) {
                // The journal never blocks or fails the real write.
                console.error(
                  "[MANIFEST-JOURNAL] pre-image journal error (non-blocking):",
                  journalError,
                );
              }
            }
          } else if (decision.kind === "failed") {
            tx.abort();
          }
          // "refuse": no request — the transaction commits empty.
        } catch (error) {
          decision = { kind: "failed", error };
          try {
            tx.abort();
          } catch {
            // already finishing
          }
        }
      };
    });
  }

  /**
   * Does `key` exist in the document store? `null` when the store cannot be read
   * (the caller decides what "unknown" means — never conflated with "absent").
   * WR-685 D9: the sidecar writer's liveness probe.
   */
  async hasKey(key: string): Promise<boolean | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const store = await this.getStore("readonly");
      return await new Promise<boolean | null>((resolve) => {
        // Bounded: a lookup that never settles answers "unknown", never hangs the
        // sidecar write that waits on it.
        timer = setTimeout(() => resolve(null), HAS_KEY_TIMEOUT_MS);
        const request = store.count(key);
        request.onsuccess = () => resolve(request.result > 0);
        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Enumerate every stored key that begins with `prefix` (registry-INDEPENDENT).
   *
   * Bombproof storage foundation, step 3 (approval-gate §6.3 / RESHAPE-3): the raw
   * two-tier `project_<id>_*` delete-sweep needs to discover the actual on-disk keys
   * for a project WITHOUT consulting any registry (the registry under-reclaims). This
   * reads `data_store`'s keyspace directly via `getAllKeys()` and filters by prefix.
   *
   * Fail-safe: if the database is unavailable, returns [] (the caller still sweeps
   * the localStorage tier). Only ever operates on this manager's own store
   * (`data_store` in `stagecraft_storage`); the dedicated asset DB `stagecraft_assets`
   * is a SEPARATE database and is never reachable from here.
   */
  async getKeysByPrefix(prefix: string): Promise<string[]> {
    try {
      const store = await this.getStore("readonly");
      return await new Promise<string[]>((resolve, reject) => {
        const request = store.getAllKeys();
        this.rejectOnTxAbort(store, reject);
        request.onsuccess = () => {
          const keys = (request.result as IDBValidKey[]).filter(
            (k): k is string => typeof k === "string" && k.startsWith(prefix),
          );
          resolve(keys);
        };
        request.onerror = () =>
          reject(
            new StorageError(
              "Failed to enumerate keys",
              StorageErrorCode.OPERATION_FAILED,
              { error: request.error },
            ),
          );
      });
    } catch {
      // Database unavailable — the localStorage tier sweep still runs.
      return [];
    }
  }

  /**
   * Close database connection
   */
  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  /**
   * Delete database
   */
  async deleteDatabase(): Promise<void> {
    await this.close();

    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.DB_NAME);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        reject(
          new StorageError(
            "Failed to delete database",
            StorageErrorCode.OPERATION_FAILED,
            { error: request.error },
          ),
        );
      };
    });
  }
}

// Export singleton instance
export const indexedDBManager = new IndexedDBManager();
